import { type CompanyFormat, formatMoney, RecordDrawer, TextButton } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { type DataColumn, SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useState } from "react";
import { RouterLink } from "../../router-link";
import { cellOf, type Zone, type ZoneSheet } from "../../zones";
import { CellPrice, ChargesList, CostsList, ZonePriceList } from "./prices";
import styles from "./zones.module.css";

type Money = Pick<CompanyFormat, "currency">;

type Row = {
  id: string;
  name: string;
  status: string;
  places: number;
  zone: Zone;
  cell: ReturnType<typeof cellOf>;
};

/**
 * The zones as a list (P8-40, `Console / Service zones / List view`): each with its places and
 * what the chosen item costs there. A zone opens in a drawer with every item's price and the
 * chosen one's charges, costs and margin.
 */
export function ZonesTable({
  zones,
  sheet,
  itemId,
  format,
  onShowOnMap,
}: {
  zones: Zone[];
  /** null: the staff member can't see prices (orders.read) */
  sheet: ZoneSheet | null;
  itemId: string | undefined;
  format: Money;
  onShowOnMap: (zoneId: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const priced = sheet !== null;
  const money = (v: string | null | undefined) => (v ? formatMoney(v, format) : "—");
  const itemName = sheet?.items.find((i) => i.id === itemId)?.name ?? "the chosen item";
  const rows: Row[] = zones.map((z) => ({
    id: z.id,
    name: z.name,
    status: z.status === "active" ? "Active" : "Off",
    places: z.location_count,
    zone: z,
    cell: cellOf(sheet ?? undefined, z.id, itemId),
  }));
  // Money stays a string (never a JS number), so its columns don't sort.
  const columns: DataColumn<Row>[] = [
    {
      key: "name",
      label: "Zone",
      render: (_, r) => (
        <span className={styles.stack}>
          <button type="button" className={styles.zoneName} onClick={() => setOpen(r.id)}>
            {r.name}
          </button>
          <span className={styles.mutedInline}>{r.zone.area_km2 ? `${r.zone.area_km2} km²` : "No area"}</span>
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (_, r) => <Badge tone={r.zone.status === "active" ? "success" : "neutral"}>{r.status}</Badge>,
    },
    { key: "places", label: "Places", numeric: true },
    ...(priced
      ? ([
          {
            key: "price",
            label: "Price",
            sortable: false,
            render: (_, r) => (
              <span className={styles.priceCell}>
                <CellPrice cell={r.cell} zoneName={r.name} format={format} />
              </span>
            ),
          },
          {
            key: "pays",
            label: "Customer pays",
            sortable: false,
            numeric: true,
            render: (_, r) => money(r.cell?.receivable_total),
          },
          {
            key: "costs",
            label: "Costs",
            sortable: false,
            numeric: true,
            render: (_, r) => money(r.cell?.payable_total),
          },
          { key: "margin", label: "Margin", sortable: false, numeric: true, render: (_, r) => money(r.cell?.margin) },
        ] satisfies DataColumn<Row>[])
      : []),
  ];
  const chosen = rows.find((r) => r.id === open);
  return (
    <div className={styles.tableWrap}>
      <SortableDataTable
        rows={rows}
        columns={columns}
        rowKey="id"
        caption={`Service zones${priced ? " and their prices" : ""}`}
        emptyMessage="No zones yet. Draw the first with New zone."
        itemName={{ one: "zone", other: "zones" }}
      />
      {priced ? (
        <div className={styles.tableFoot}>
          <span>Prices in force today, for {itemName}</span>
          <span>
            <span className={styles.sourceOwn}>Zone name</span>: the zone sets the price · otherwise the any-zone price
            applies
          </span>
        </div>
      ) : null}
      {chosen ? (
        <ZoneDrawer
          row={chosen}
          sheet={sheet}
          itemName={itemName}
          format={format}
          onClose={() => setOpen(null)}
          onShowOnMap={onShowOnMap}
        />
      ) : null}
    </div>
  );
}

/** One zone beside the list: every item's price, the chosen item's charges and costs, and ways on. */
function ZoneDrawer({
  row,
  sheet,
  itemName,
  format,
  onClose,
  onShowOnMap,
}: {
  row: Row;
  sheet: ZoneSheet | null;
  itemName: string;
  format: Money;
  onClose: () => void;
  onShowOnMap: (zoneId: string) => void;
}) {
  const { zone: z, cell } = row;
  return (
    <RecordDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={z.name}
      description={`${row.status} · ${z.location_count === 1 ? "1 place" : `${z.location_count} places`}${z.area_km2 ? ` · ${z.area_km2} km²` : ""}`}
    >
      {sheet ? (
        <div className={styles.cards}>
          <section className={styles.priceCard} aria-label="Every item">
            <h3 className={styles.cardTitle}>Prices · customer pays</h3>
            <ZonePriceList sheet={sheet} zoneId={z.id} zoneName={z.name} format={format} inline />
          </section>
          {cell && !cell.missing && cell.receivable_total && cell.margin ? (
            <>
              <section className={styles.priceCard} aria-label="Charges">
                <h3 className={styles.cardTitle}>{itemName} · one delivery</h3>
                <ChargesList lines={cell.lines} receivable={cell.receivable_total} zoneName={z.name} format={format} />
              </section>
              <section className={styles.priceCard} aria-label="Costs">
                <h3 className={styles.cardTitle}>Costs · MajiTap pays</h3>
                <CostsList lines={cell.lines} margin={cell.margin} zoneName={z.name} format={format} />
              </section>
            </>
          ) : null}
        </div>
      ) : (
        <p className={styles.muted}>Prices need the orders.read permission.</p>
      )}
      <div className={styles.actions}>
        <TextButton onClick={() => onShowOnMap(z.id)}>Show on the map</TextButton>
        <RouterLink href={`/pricing?geofence_id=${z.id}`} className={styles.link}>
          Its rules in Pricing
        </RouterLink>
      </div>
    </RecordDrawer>
  );
}
